import Column from "@amzn/meridian/esm/components/column";
import { css } from "@emotion/css";
import Row from "@amzn/meridian/esm/components/row";
import Text from "@amzn/meridian/esm/components/text";
import Tile from "@amzn/meridian/esm/components/tile";
import pencilTokens from "@amzn/meridian-tokens/base/icon/pencil";
import trashTokens from "@amzn/meridian-tokens/base/icon/trash";
import { THEME_COLOR } from "../../../styles/colors";
import { flashClass } from "../../../styles/motion";
import { gridStackClass } from "../insights.styles";
import DeleteConfirmButtons from "../DeleteConfirmButtons";
import HeightReserver from "../HeightReserver";
import { ACCENT_COLOR, actionRowLayout } from "./actionItems.constants";
import { DELETE_CONFIRM_LABEL, deleteButtonTheme } from "../insights.constants";
import { buildSubtitle } from "./actionItems.utils";
import IconButton from "../IconButton";
import { useHoverReveal } from "../insights.hooks";

// Static appearance of the newly-added flash overlay; the fade-out is driven by flashClass.
const flashOverlayClass = css({
  position: "absolute",
  inset: 0,
  zIndex: 1,
  borderRadius: "8px",
  backgroundColor: THEME_COLOR.MINT_GREEN,
  pointerEvents: "none",
});

// Content + owner/deadline subtitle column. Factored out so the display state and the
// confirm-delete state's (visibility:hidden) height-reserver render the SAME markup — the two
// can never drift out of height parity because they are literally the same sub-render.
const CardContent = ({ item }) => (
  <Column spacing="none">
    <Text type="b200">{item.content}</Text>
    <Text type="b100" color="secondary">
      {buildSubtitle(item)}
    </Text>
  </Column>
);

// Normal display state: content fills, then the edit pencil (slot A) and delete trash (slot B).
// The edit/delete controls carry controlsStyle (hover/focus reveal). Delete is destructive, so it
// gets the red deleteButtonTheme via IconButton's tokens prop.
const DisplayRow = ({ item, onEdit, onDelete, controlsStyle }) => (
  <Row {...actionRowLayout}>
    <CardContent item={item} />
    {/* Slot A (primary): edit pencil. */}
    <IconButton
      icon={pencilTokens}
      onClick={onEdit}
      style={controlsStyle}
      ariaLabel="Edit action item"
    />
    {/* Slot B (secondary): delete trash (destructive → deleteButtonTheme). */}
    <IconButton
      icon={trashTokens}
      onClick={onDelete}
      tokens={deleteButtonTheme}
      style={controlsStyle}
      ariaLabel="Delete action item"
    />
  </Row>
);

// Inline confirm-delete state: same row layout / icon slots as DisplayRow so nothing shifts when
// toggling modes.
const ConfirmDeleteRow = ({ item, onConfirmDelete, onCancelDelete }) => (
  <Row {...actionRowLayout}>
    {/* Grid stack: the height-reserving content and the confirm prompt share ONE grid cell (both
        pinned to grid-area 1/1 by gridStackClass) so the always-laid-out content drives the height
        and the prompt overlays it — no invisible duplicate markup, no absolute positioning. */}
    <div className={gridStackClass}>
      {/* Display block — kept in layout (visibility:hidden) while confirming so it keeps reserving
          the exact content+subtitle height regardless of how many lines the content wraps to;
          hidden from AT since the prompt speaks for this row. Same CardContent as DisplayRow, so
          the reserved height always matches the display state. */}
      <HeightReserver>
        <CardContent item={item} />
      </HeightReserver>
      {/* Prompt block — stretches to the shared cell and vertically centers via Meridian Row
          alignmentVertical (replaces the old inline absolute/inset + flex centering). */}
      <Row alignmentVertical="center" spacing="none">
        <Text type="b200" color="secondary">{DELETE_CONFIRM_LABEL}</Text>
      </Row>
    </div>
    {/* Slots A and B: confirm trash + cancel undo. A fragment, so each button still lands in its
        own actionRowLayout widths slot. */}
    <DeleteConfirmButtons onConfirm={onConfirmDelete} onCancel={onCancelDelete} />
  </Row>
);

/**
 * ActionItemCard - A single action item rendered as a Meridian Tile with its
 * content, owner/deadline subtitle, and edit/delete icon buttons.
 *
 * The edit/delete controls are hidden by default and revealed when the row is
 * hovered or receives keyboard focus (focus reveal keeps them reachable without
 * a mouse). They stay in layout (opacity toggle, not mount/unmount) so the row
 * doesn't reflow when they appear. Clicking delete asks for inline confirmation
 * before removing the item.
 *
 * A single `confirming` fork picks the whole row: ConfirmDeleteRow (inline
 * "Delete this item?" prompt) vs DisplayRow (content + edit/delete). Both share
 * the CardContent sub-render and actionRowLayout, keeping the two states in
 * height/slot parity.
 */
const ActionItemCard = ({ item, onEdit, onDelete, onConfirmDelete, onCancelDelete, confirming = false, highlighted = false }) => {
  const { bind, controlsStyle } = useHoverReveal();

  return (
    <div
      style={{ position: "relative" }}
      {...bind}
    >
      <Tile accent={ACCENT_COLOR} spacingInset="300 350">
        {confirming ? (
          <ConfirmDeleteRow item={item} onConfirmDelete={onConfirmDelete} onCancelDelete={onCancelDelete} />
        ) : (
          <DisplayRow item={item} onEdit={onEdit} onDelete={onDelete} controlsStyle={controlsStyle} />
        )}
      </Tile>
      {highlighted && <div className={`${flashClass} ${flashOverlayClass}`} />}
    </div>
  );
};

export default ActionItemCard;
